import {getStoreBuilder, BareActionContext} from "vuex-typex";
import RootState from "../index";
import PlayServerClient from "../../modules/PlayServerClient";
import {ActiveSio, CardUserdata, Company, CompanyConfig, PersonConfig} from "../../modules/models";

export interface BaseState {
    company: Company | null;
    customerRef: number | null;
    user: CardUserdata | null;
    activeSio: ActiveSio | null;
    config: PersonConfig;
    companyConfig: CompanyConfig;
    unreadUsermessagesCount: number;
}

export default interface BaseApi {
    readonly company: Company | null;
    readonly customerRef: number | null;
    loadCompany(orgnum: number): Promise<void>;
    loadCompanyPromise(): Promise<void>; // Temporary
    readonly user: CardUserdata | null;
    loadUser(): Promise<void>;
    readonly activeSio: ActiveSio | null;
    loadActiveSio(): Promise<void>;
    readonly config: PersonConfig;
    loadConfig(): Promise<void>;
    readonly authenticated: boolean;
    readonly companyConfig: CompanyConfig;
    readonly unreadUsermessagesCount: number;
    loadUnreadUsermessagesCount(): Promise<void>;
}

export function initBaseModule(sc: PlayServerClient): BaseApi {
    const storeBuilder = getStoreBuilder<RootState>();

    const state = {
        company: null,
        customerRef: null,
        user: null,
        activeSio: null,
        config: PersonConfig.default(),
        companyConfig: CompanyConfig.default(),
        unreadUsermessagesCount: 0,
    };
    const moduleBuilder = storeBuilder.module<BaseState>("base", state);

    // mutations
    const mutations = {
        setCompany: (state: BaseState, company: Company | null) => state.company = company,
        setCustomerRef: (state: BaseState, customerRef: number | null) => state.customerRef = customerRef,
        setUser: (state: BaseState, user: CardUserdata | null) => state.user = user,
        setActiveSio: (state: BaseState, activeSio: ActiveSio | null) => state.activeSio = activeSio,
        setConfig: (state: BaseState, config: PersonConfig) => state.config = config,
        setCompanyConfig: (state: BaseState, companyConfig: CompanyConfig) => state.companyConfig = companyConfig,
        setUnreadUsermessagesCount: (state: BaseState, unreadUsermessagesCount: number) => state.unreadUsermessagesCount = unreadUsermessagesCount,
    };

    // commits
    const commits = {
        setCompany: moduleBuilder.commit(mutations.setCompany),
        setCustomerRef: moduleBuilder.commit(mutations.setCustomerRef),
        setUser: moduleBuilder.commit(mutations.setUser),
        setActiveSio: moduleBuilder.commit(mutations.setActiveSio),
        setConfig: moduleBuilder.commit(mutations.setConfig),
        setCompanyConfig: moduleBuilder.commit(mutations.setCompanyConfig),
        setUnreadUsermessagesCount: moduleBuilder.commit(mutations.setUnreadUsermessagesCount),
    };

    // actions
    const actions = {
        async loadCompany(context: BareActionContext<BaseState, RootState>, orgnum: number) {
            const company = orgnum <= 0 ? await sc.fetchDefaultCompany() : await sc.fetchCompany(orgnum);
            const customerRef = company != null ? await sc.fetchCustomerRef(company.orgNum) : null;
            commits.setCompany(company);
            commits.setCustomerRef(customerRef);
            // Load company config when company is loaded
            const conf: CompanyConfig = company != null ? (await sc.getCompanyConfig(company.orgNum)) : CompanyConfig.default();
            commits.setCompanyConfig(conf)
        },
        async loadUser(context: BareActionContext<BaseState, RootState>) {
            const user = await sc.currentUser();
            if(user != null) {
                await dispatchers.loadUnreadUsermessagesCount();
            } else {
                commits.setUnreadUsermessagesCount(0);
            }
            commits.setUser(user);
            if(!context.state.config.loaded) { // auto load users personconfig when user has been loaded.
                dispatchers.loadConfig();
            }
        },
        async loadActiveSio(context: BareActionContext<BaseState, RootState>) {
            const activeSio = await sc.activePersonSio();
            commits.setActiveSio(activeSio);
        },
        async loadConfig(context: BareActionContext<BaseState, RootState>) {
            const config = await sc.personConfig();
            commits.setConfig(config);
        },
        async loadUnreadUsermessagesCount(context: BareActionContext<BaseState, RootState>) {
            const count = await sc.unreadUsermessagesCount();
            commits.setUnreadUsermessagesCount(count);
        }
    };

    // getters
    const getters = {
        get state() {
            return moduleBuilder.state()();
        },
        get company() {
            return this.state.company;
        },
        get customerRef() {
            return this.state.customerRef;
        },
        get user() {
            return this.state.user;
        },
        get activeSio() {
            return this.state.activeSio;
        },
        get config() {
            return this.state.config;
        },
        get authenticated() {
            return this.state.user != null && this.state.user.person.guid !== "";
        },
        get companyConfig() {
            return this.state.companyConfig;
        },
        get unreadUsermessagesCount() {
            return this.state.unreadUsermessagesCount;
        },
    };
    // dispatchers
    const dispatchers = {
        loadCompany: moduleBuilder.dispatch(actions.loadCompany),
        loadUser: moduleBuilder.dispatch(actions.loadUser),
        loadActiveSio: moduleBuilder.dispatch(actions.loadActiveSio),
        loadConfig: moduleBuilder.dispatch(actions.loadConfig),
        loadUnreadUsermessagesCount: moduleBuilder.dispatch(actions.loadUnreadUsermessagesCount),
    };

    let loadingCompany: Promise<void>;

    // public api
    return {
        // getters
        get company() {
            return getters.company;
        },
        get customerRef() {
            return getters.customerRef;
        },
        get user() {
            return getters.user;
        },
        get activeSio() {
            return getters.activeSio;
        },
        get config() {
            return getters.config;
        },
        get authenticated() {
            return getters.authenticated;
        },
        get companyConfig() {
            return getters.companyConfig;
        },
        get unreadUsermessagesCount() {
            return getters.unreadUsermessagesCount;
        },

        // actions
        loadCompany: (orgnum: number) => { loadingCompany = dispatchers.loadCompany(orgnum); return loadingCompany },
        loadUser: dispatchers.loadUser,
        loadActiveSio: dispatchers.loadActiveSio,
        loadConfig: dispatchers.loadConfig,
        loadUnreadUsermessagesCount: dispatchers.loadUnreadUsermessagesCount,

        // special temporary
        loadCompanyPromise(): Promise<void> {
            return loadingCompany;
        }
    };
}
